import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DollarSign, Plus, ArrowRight, Download } from 'lucide-react';
import { Logo } from '../components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// دالة تنسيق المبالغ بالأرقام الإنجليزية وشعار الريال السعودي الجديد
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '0 ﷼';
  return `${Number(amount).toLocaleString('en-US')} ﷼`;
};

export default function Invoices() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [user, setUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    invoice_number: '',
    client_id: '',
    client_name: '',
    amount: '',
    due_date: '',
    description: '',
  });

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      setUser(JSON.parse(userStr));
    }
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/invoices`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInvoices(response.data);
    } catch (err) {
      console.error('Error fetching invoices:', err);
    }
  };

  const downloadPDF = async (invoiceId, invoiceNumber) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/invoices/${invoiceId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      
      // إنشاء رابط للتحميل
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      alert('حدث خطأ أثناء تحميل الملف');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/invoices`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShowModal(false);
      fetchInvoices();
      setFormData({
        invoice_number: '',
        client_id: '',
        client_name: '',
        amount: '',
        due_date: '',
        description: '',
      });
    } catch (err) {
      console.error('Error creating invoice:', err);
    }
  };

  return (
    <div className="min-h-screen bg-sand" dir="rtl" lang="ar">
      <header className="bg-primary py-4 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ArrowRight className="w-6 h-6 text-white" />
            </button>
            <Logo size="md" />
            <h1 className="text-2xl font-heading font-bold text-white">الفواتير</h1>
          </div>
          {user?.role === 'lawyer' && (
            <button
              data-testid="add-invoice-btn"
              onClick={() => setShowModal(true)}
              className="flex items-center gap-2 px-6 py-2 bg-gold text-white rounded-md hover:bg-gold-dark transition-all duration-300"
            >
              <Plus className="w-5 h-5" />
              إضافة فاتورة
            </button>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {invoices.map((inv) => (
            <div
              key={inv.id}
              className="bg-white rounded-lg p-6 shadow-sm border border-gray-100 hover-lift"
              data-testid={`invoice-card-${inv.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <DollarSign className="w-8 h-8 text-gold" />
                <span
                  className={`px-3 py-1 rounded-full text-xs font-body ${
                    inv.status === 'paid'
                      ? 'bg-green-100 text-green-700'
                      : inv.status === 'pending'
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-red-100 text-red-700'
                  }`}
                >
                  {inv.status === 'paid'
                    ? 'مدفوعة'
                    : inv.status === 'pending'
                    ? 'معلقة'
                    : inv.status}
                </span>
              </div>
              <h3 className="text-lg font-heading font-semibold text-primary mb-2">
                فاتورة رقم {inv.invoice_number}
              </h3>
              <p className="text-sm text-gray-600 mb-2 font-body">العميل: {inv.client_name}</p>
              <p className="text-2xl font-bold text-gold mb-2 font-heading">
                {formatCurrency(inv.amount)}
              </p>
              <p className="text-sm text-gray-600 mb-2 font-body">{inv.description}</p>
              <p className="text-sm text-gray-500 font-body mb-4">
                تاريخ الاستحقاق:{' '}
                {new Date(inv.due_date).toLocaleDateString('ar-EG')}
              </p>
              <button
                onClick={() => downloadPDF(inv.id, inv.invoice_number)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-900 transition-all font-body"
                data-testid={`download-invoice-${inv.id}`}
              >
                <Download className="w-4 h-4" />
                تحميل PDF
              </button>
            </div>
          ))}
        </div>

        {invoices.length === 0 && (
          <div className="text-center py-16" data-testid="no-invoices">
            <DollarSign className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 font-body">لا توجد فواتير حاليا</p>
          </div>
        )}
      </main>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 max-w-2xl w-full mx-4" data-testid="invoice-modal">
            <h2 className="text-2xl font-heading font-bold text-primary mb-6">
              إضافة فاتورة جديدة
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  data-testid="invoice-number-input"
                  type="text"
                  placeholder="رقم الفاتورة"
                  value={formData.invoice_number}
                  onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                  required
                  className="px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
                />
                <input
                  data-testid="invoice-amount-input"
                  type="number"
                  placeholder="المبلغ"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required
                  className="px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  data-testid="invoice-client-id-input"
                  type="text"
                  placeholder="معرف العميل"
                  value={formData.client_id}
                  onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                  required
                  className="px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
                />
                <input
                  data-testid="invoice-client-name-input"
                  type="text"
                  placeholder="اسم العميل"
                  value={formData.client_name}
                  onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                  required
                  className="px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
                />
              </div>
              <input
                data-testid="invoice-due-date-input"
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
              />
              <textarea
                data-testid="invoice-description-input"
                placeholder="وصف الفاتورة"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                rows={3}
                className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
              />
              <div className="flex gap-4 justify-end">
                <button
                  data-testid="invoice-cancel-btn"
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-all font-body"
                >
                  إلغاء
                </button>
                <button
                  data-testid="invoice-submit-btn"
                  type="submit"
                  className="px-6 py-2 bg-gold text-white rounded-md hover:bg-gold-dark transition-all font-body"
                >
                  حفظ
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
